import { DiffEditor } from '@monaco-editor/react'
import { Box, CircularProgress } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TextEditorLanguage } from '@/features/controller/configuration/types'
import { useMonacoThemes } from '@/features/controller/configuration/utils/editor/hooks/useMonacoThemes'
import { EDITOR_OPTIONS, MONACO_THEMES } from '@/features/controller/configuration/utils/editor/monacoThemes'

const DEFAULT_HEIGHT = 500
const MIN_FITTED_HEIGHT = 60

interface DiffViewerProps {
  /** Collapses unchanged lines and shrinks to what is left, up to the default height */
  fitToChanges?: boolean
  modifiedText: string
  originalText: string
  language?: TextEditorLanguage
}

export function DiffViewer({
  fitToChanges = false,
  modifiedText,
  originalText,
  language = TextEditorLanguage.ini,
}: Readonly<DiffViewerProps>) {
  const { t } = useTranslation()
  const { themesReady } = useMonacoThemes()
  const [height, setHeight] = useState(DEFAULT_HEIGHT)

  const isJson = language === 'json'

  // Build consistent editor options for DiffEditor
  const editorOptions = {
    ...EDITOR_OPTIONS.base,
    ...EDITOR_OPTIONS.readonlyMode,
    links: false,
    wordWrap: 'on',
    readOnly: true,
    // Related to the issue in the library where the wrap option works only on the right side
    useInlineViewWhenSpaceIsLimited: false,
    renderSideBySide: true,
    hideUnchangedRegions: { enabled: fitToChanges },
    // Lets the page scroll past a fitted diff instead of the diff capturing the wheel
    scrollbar: { alwaysConsumeMouseWheel: !fitToChanges },
    // JSON-specific formatting
    ...(isJson && {
      tabSize: 2,
      insertSpaces: true,
      formatOnPaste: true,
      formatOnType: true,
    }),
  }

  return themesReady ? (
    <DiffEditor
      height={height}
      language={language}
      modified={modifiedText}
      options={editorOptions}
      original={originalText}
      theme={MONACO_THEMES.readonlyMode.name}
      onMount={(editor) => {
        editor.revealFirstDiff()
        if (fitToChanges) {
          const sides = [editor.getOriginalEditor(), editor.getModifiedEditor()]
          // Fires again once the diff is computed and unchanged lines are hidden, and when the user expands them
          const fitHeight = () => {
            // The horizontal scrollbar sits on top of the content, so make room for it as well
            const contentHeight = Math.max(
              ...sides.map((side) => side.getContentHeight() + side.getLayoutInfo().horizontalScrollbarHeight),
            )
            setHeight(Math.min(Math.max(contentHeight, MIN_FITTED_HEIGHT), DEFAULT_HEIGHT))
          }
          sides.forEach((side) => side.onDidContentSizeChange(fitHeight))
          fitHeight()
        }
      }}
    />
  ) : (
    <Box sx={{ height: DEFAULT_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress title={t('controllers.configuration.loading_diff_viewer')} />
    </Box>
  )
}
